import { useMemo } from 'react';
import MultiLineChart from './MultiLineChart';
import { DEFAULT_TIME_ZONE } from '../../utils/dateTime';

const HISTORY_DAYS = 90;
const KIMBER_COLOR = '#4472C4';
const HSU_COLOR = '#ED7D31';
const Y_TICKS = Object.freeze([0, 1, 2, 3, 4, 5, 6]);

function startOfLocalDay(date) {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
}

function roundLoss(value) {
  return Math.round(Math.min(6, Math.max(0, value)) * 10) / 10;
}

export function buildSoilingLossHistory(days = HISTORY_DAYS, now = new Date()) {
  const endDate = startOfLocalDay(now);
  const startDate = new Date(endDate);
  startDate.setDate(endDate.getDate() - (days - 1));

  const kimber = [];
  const hsu = [];
  let kimberLoss = 0.4;
  let hsuLoss = 0.7;
  const rainDays = new Set([0, 31, 67]);

  for (let i = 0; i < days; i += 1) {
    const date = new Date(startDate);
    date.setDate(startDate.getDate() + i);

    if (rainDays.has(i)) {
      kimberLoss = 0.4;
      hsuLoss = 0.55;
    } else {
      kimberLoss += 0.055;
      const pmDrift = Math.sin(i / 4.2) * 0.12 + Math.sin(i / 11) * 0.08;
      const pmStep = i % 9 === 0 ? 0.18 : i % 14 === 0 ? -0.12 : 0.05;
      hsuLoss += pmStep + pmDrift;
    }

    kimber.push({ date, value: roundLoss(kimberLoss) });
    hsu.push({ date, value: roundLoss(hsuLoss) });
  }

  return { startDate, endDate, kimber, hsu };
}

export default function SoilingLossFraction({
  onTooltip,
  timeZone = DEFAULT_TIME_ZONE,
}) {
  const history = useMemo(() => buildSoilingLossHistory(), []);

  const series = useMemo(
    () => [
      {
        data: history.kimber,
        color: KIMBER_COLOR,
        label: 'Kimber (Fixed Rate)',
      },
      {
        data: history.hsu,
        color: HSU_COLOR,
        label: 'HSU (PM-driven)',
        dash: '6,4',
      },
    ],
    [history],
  );

  return (
    <MultiLineChart
      startDate={history.startDate}
      endDate={history.endDate}
      series={series}
      domain={[0, 6]}
      tickValues={Y_TICKS}
      tickFormat={(d) => `${Number(d).toFixed(1)}%`}
      unit="%"
      tooltipUnit="%"
      fixedYDomain
      yGrid
      dailyTicks
      showLegend
      showPoints={false}
      showHoverDots={false}
      height={280}
      wrapperClassName="soiling-loss-fraction"
      onTooltip={onTooltip}
      timeZone={timeZone}
    />
  );
}
